import { generateImageData } from "@/components/atoms/media/umbracoMediaElementPropsFactory";
import { CardProps } from "@/components/molecules/card";
import { PageInfo } from "@/components/molecules/pagination/Pagination";
import { Filters } from "@/stores/filteredCardStore";
import { Cultures, GridColumns, PageTypes } from "@/types/enums";
import { FilterOptionsContext } from "@/utils/filterOptionsContext";
import { ClientEnvironmentContext } from "@/utils/globalConfig";
import DOMPurify from "isomorphic-dompurify";
import { useContext } from "react";
import { mappedCardType, mappedTagForItem } from "../cardGrid/CardGridFactory";
import { OverviewBlock, OverviewContentQueryResult } from "./overviewBlock";
import { FundsCardProps } from "@/components/molecules/fundsOverviewCard";
import { FundsTagType } from "@/types/consts";

function OverviewBlockFactory(
  data: any, //TODO: Convert to type unknown
  key: string | number,
  pagePath: string,
  culture: Cultures,
  pageContentType?: PageTypes,
): JSX.Element {
  const clientEnvironment = useContext(ClientEnvironmentContext);
  const filterOptionsContext = useContext(FilterOptionsContext);

  const content = data?.content?.properties;

  if (!filterOptionsContext) {
    throw new Error("FilterOptionsContext is not available");
  }

  const colSpan = (): number => {
    if (data.gridColumns === GridColumns.One) return 12;
    else if (data.gridColumns === GridColumns.Two) return 6;
    else return 4;
  };

  /**
   * Fetches content for the overview pages and returns an array of `CardProps` objects.
   * This function handles the content query based on the current page number, processes the result, and passes it to the provided callback for further use.
   *
   * @param page - The current page number in the navigation, used to fetch the correct slice of content for that page.
   * @param selectedFilters - An object containing the currently selected filters, which can be used to refine the content query.
   * @param setResultCallback - A callback function that receives the query result as a `CardGridContentQueryResult` object, which includes the
   * array of `CardProps` and additional metadata.
   *
   * @returns An array of `CardProps` objects, representing the content cards to be displayed for the specified page.
   */
  function contentQuery(
    pageInfo: PageInfo,
    selectedFilters: Filters,
    setResultCallback: (result: OverviewContentQueryResult) => void,
  ) {
    // If no pageInfo provided, default back to some generic defaults
    pageInfo.pageSize = pageInfo.pageSize ?? content.detailItemsPerPage ?? 12;
    pageInfo.page = pageInfo.page ?? 1;

    const resolveSorting = (sortBy: string, sortOrder: string) => {
      let sort = "";
      let order = "";

      sortBy = sortBy?.toLowerCase();
      sortOrder = sortOrder?.toLowerCase();

      switch (sortBy) {
        case "sorting order":
          sort = "sortOrder";
          break;
        case "create date":
          sort = "createDate";
          break;
        case "publication date":
          sort = "publicationDate";
          break;
        default:
          sort = "createDate";
      }

      switch (sortOrder) {
        case "ascending":
          order = "asc";
          break;
        case "descending":
          order = "desc";
          break;
        default:
          order = "asc";
      }

      return `${sort}:${order}`;
    };

    const searchParams = new URLSearchParams({
      fetch: `children:${pagePath}`,
      fields: "properties[$all[properties[$all[properties[$all]]]]]",
      sort: resolveSorting(content.sortBy, content.orderBy),
    });

    // No items need to be skipped on the first page
    if (pageInfo.page !== 1) {
      searchParams.append("skip", `${(pageInfo.page - 1) * pageInfo.pageSize}`);
    }

    searchParams.append("take", `${pageInfo.pageSize}`);

    // Build up params with all selected filters
    Object.entries(selectedFilters).forEach(([category, filters]) => {
      let categoryLookup: string = "";
      switch (category) {
        case "articleType":
          categoryLookup = "articleTypes";
          break;
        case "portfolioType":
          categoryLookup = "portfolioTypes";
          break;
        case "fund":
          categoryLookup = "funds";
          break;
        case "strategy":
          categoryLookup = "strategies";
          break;
        case "year":
          categoryLookup = "publicationYear";
          break;
      }
      if (filters.length > 0 && categoryLookup) {
        searchParams.append("filter", `${categoryLookup}:${filters.join(",")}`);
      }
    });

    const queryUrl = `/api/overviewdata/?${searchParams.toString()}`;

    fetch(queryUrl, { keepalive: true })
      .then((res) => res.json())
      .then((res) => {
        const cards = res?.items?.map((item: any) =>
          cardFromUmbracoPageItem(item, clientEnvironment!.umbracoBaseUrl),
        );
        setResultCallback({
          currentPageCards: cards,
          totalItemCount: res.total,
        });
      });

    return;
  }

  return (
    <OverviewBlock
      key={`card-grid-${key}`}
      contentQuery={contentQuery}
      filterOptions={filterOptionsContext.filterOptions}
      culture={culture}
      showFilters={
        pageContentType !== PageTypes.FundsMandatesOverviewPage
          ? {
              portfolioTypes:
                pageContentType === PageTypes.PortfolioOverviewPage,
              strategies: pageContentType === PageTypes.PortfolioOverviewPage,
              articleTypes:
                pageContentType === PageTypes.PublicationOverviewPage,
              funds: pageContentType === PageTypes.PublicationOverviewPage,
              years: pageContentType === PageTypes.PublicationOverviewPage,
            }
          : undefined
      }
      gridColumns={colSpan()}
      pageSize={content.detailItemsPerPage ?? 12}
    />
  );
}

/**
 * Converts an Umbraco page item into a CardProps object.
 *
 * @param item - The Umbraco page item to convert.
 * @param umbracoBaseUrl - The base URL for Umbraco media items.
 * @returns A CardProps object representing the converted item.
 */
function cardFromUmbracoPageItem(
  item: any,
  umbracoBaseUrl: string,
): CardProps | FundsCardProps {
  const id = item?.id;
  const headerContent = item?.properties?.header?.items[0]?.content?.properties;
  const content = item?.properties;

  // Item title - title primary, header title fallback, page name last fallback.
  const sanitizedTitle = headerContent?.title?.markup
    ? DOMPurify.sanitize(headerContent?.title?.markup, { ALLOWED_TAGS: [] })
    : null;

  const title = content?.title ?? sanitizedTitle ?? item.name;

  // item image - primary header image, ogImage as fallback. When header contains something else except image, fallback will be used.
  const headerImage =
    item.contentType === PageTypes.FundsDetailPage
      ? content?.headerImage?.items?.[0]?.content?.properties?.image?.[0]
      : headerContent?.mediaItem?.items?.[0]?.content?.properties?.image?.[0];
  const ogImage = content?.openGraphImage?.[0];
  const overviewImage = content?.overviewImage?.[0];

  const mediaItem = generateImageData(
    {
      image: [overviewImage ?? headerImage ?? ogImage],
    },
    umbracoBaseUrl,
  );

  const card = {
    id: id,
    title: title,
    tag: mappedTagForItem(item.contentType, content),
    cardType: mappedCardType(item.contentType),
    text:
      content?.text ?? headerContent?.subtitle ?? content?.introText?.markup,
    mediaData: mediaItem,
    link: {
      url: item?.route?.path,
    },
    date: content?.publicationDate ?? item?.createDate,
  };

  if (item.contentType === PageTypes.FundsDetailPage) {
    return {
      ...card,
      contentType: PageTypes.FundsDetailPage,
      isOpenForInvestment: item?.properties?.openForInvestment,
      tags: [
        {
          type: FundsTagType.MinimumInvestment,
          value: item?.properties?.minimumInvestment?.[0]?.name ?? "",
        },
        {
          type: FundsTagType.InvestmentType,
          value: item?.properties?.investmentType?.[0]?.name ?? "",
        },
        {
          type: FundsTagType.TargetAudience,
          value: item?.properties?.targetAudience?.[0]?.name ?? "",
        },
        {
          type: FundsTagType.RiskReturnProfile,
          value: item?.properties?.riskReturnProfile?.[0]?.name ?? "",
        },
        {
          type: FundsTagType.PropertyType,
          value: item?.properties?.propertyType?.[0]?.name ?? "",
        },
      ].filter((tag) => tag.value.trim() !== ""),
    };
  }

  return card;
}

export { OverviewBlockFactory as overviewBlockFactory };
